import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Zap, 
  Users, 
  CheckCircle, 
  UserCheck, 
  Building2, 
  Car, 
  Star,
  ArrowRight
} from 'lucide-react';
import { validationServices, testimonials } from '../data/mock';

const Home = ({ user }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge variant="secondary" className="bg-blue-600/20 text-blue-400 border-blue-600/30">
              ✨ Plataforma líder en validación de datos
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold leading-tight">
              Decisiones más seguras y
              <span className="text-blue-400"> basadas en datos</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Accede a un análisis completo del perfil de riesgo de personas, empresas y vehículos, 
              alineado con procesos AML y KYC. Protege tu empresa tomando decisiones informadas.
            </p>
            
            {user && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/dashboard">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8" data-testid="home-go-dashboard-btn">
                    Ir al Dashboard
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nuestra plataforma de validación
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Herramientas completas para la validación de identidad, contrapartes y antecedentes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {validationServices.map((service) => {
              const IconComponent = service.icon === 'user-check' ? UserCheck : 
                                  service.icon === 'building-2' ? Building2 : Car;
              
              return (
                <Card key={service.id} className="hover:shadow-lg transition-shadow border-0 bg-gradient-to-br from-white to-gray-50">
                  <CardHeader className="text-center">
                    <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-fit">
                      <IconComponent className="h-8 w-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-gray-900">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  ¿Por qué elegir NIKA?
                </h2>
                <p className="text-lg text-gray-600">
                  Moderniza tus procesos de validación con tecnología avanzada y toma decisiones más rápidas y seguras.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Zap className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Agiliza tus procesos</h3>
                    <p className="text-gray-600">
                      Reduce los tiempos de validación de 15 días a solo 3 días, mejorando la efectividad 
                      en procesos de selección y contratación.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Shield className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Prevención de riesgos</h3>
                    <p className="text-gray-600">
                      Recibe informes completos con monitoreo continuo. Cumple con regulaciones 
                      estrictas y elimina la incertidumbre en cada vinculación.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Fácil integración</h3>
                    <p className="text-gray-600">
                      Dashboard web, API REST y validación por WhatsApp. Integra nuestra solución 
                      en tus flujos de trabajo existentes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:text-center">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="grid grid-cols-2 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-blue-600 mb-2">+200</div>
                    <div className="text-sm text-gray-600">Empresas confían en nosotros</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-green-600 mb-2">95%</div>
                    <div className="text-sm text-gray-600">Precisión en validaciones</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-purple-600 mb-2">&lt; 1min</div>
                    <div className="text-sm text-gray-600">Tiempo de respuesta</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-orange-600 mb-2">24/7</div>
                    <div className="text-sm text-gray-600">Disponibilidad</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Lo que dicen nuestros clientes
            </h2>
            <p className="text-lg text-gray-600">
              Experiencias que reflejan nuestro compromiso y calidad
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.content}"</p>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                    <div className="text-sm text-blue-600">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              ¿Listo para modernizar tus procesos?
            </h2>
            <p className="text-xl text-blue-100">
              Acelera tus procesos de vinculación tomando decisiones rápidas y seguras con tecnología avanzada
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
                  Hablar con un experto
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;